import React, { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { Switch, useRouteMatch } from 'react-router-dom';
import styled from 'styled-components';

import { RouteWhenAuthenticated } from 'common/components';
import { PageHeader } from 'modules/experience-core';

import { InsightPageControls } from './components/InsightPageControl';
import InsightPageHeader from './components/InsightPageHeader';
import useDateRange from './hooks/useDateRange';
import { useInsightSubRoutes } from './hooks/useInsightsSubRoutes';
import { useSettings } from './hooks/useSettings';
import CustomerBasePage from './pages/CustomerBasePage';
import IndexPage from './pages/IndexPage';
import MenuPage from './pages/MenuPage';
import OperationsPage from './pages/OperationsPage';
import PerformancePage from './pages/PerformancePage';
import RatingsAndReviewsPage from './pages/RatingsAndReviewsPage';
import RejectedPurchasesPage from './pages/RejectedPurchasesPage';
import ReportsPage from './pages/ReportsPage';
import { InsightEntity } from './types';

type InsightRoutesProps = {
  isExperienceFlow?: boolean;
  insightEntity: InsightEntity;
};

const Root = styled.div`
  display: flex;
  position: relative;
  flex-direction: column;
  flex: 1 1 auto;
`;

const InsightRoutes = ({ insightEntity, isExperienceFlow = false }: InsightRoutesProps) => {
  const { path } = useRouteMatch();
  const { dateRange, setDateRange, dateInterval } = useDateRange();
  const { isVATEnabled, setIsVATEnabled } = useSettings();

  const pageProps = { insightEntity, dateInterval, isVATEnabled, venues: insightEntity.venues };
  const headerProps = useMemo(
    () => ({
      insightEntity,
      dateInterval,
      dateRange,
      isVATEnabled,
      setDateRange,
      setIsVATEnabled,
    }),
    [insightEntity, dateInterval, dateRange, isVATEnabled, setIsVATEnabled, setDateRange],
  );

  const baseURI = path.endsWith('/') ? path.slice(0, -1) : path;

  const { indexLink, operationsLink } = useInsightSubRoutes();

  return (
    <Root>
      <Switch>
        <RouteWhenAuthenticated exact path={baseURI}>
          {isExperienceFlow ? (
            <PageHeader title={<FormattedMessage id="insights.title" />}>
              <InsightPageControls {...headerProps} />
            </PageHeader>
          ) : (
            <InsightPageHeader title={<FormattedMessage id="insights.title" />} {...headerProps} />
          )}
          <IndexPage {...pageProps} />
        </RouteWhenAuthenticated>
        <RouteWhenAuthenticated exact path={`${baseURI}/operations`}>
          {isExperienceFlow ? (
            <PageHeader
              title={<FormattedMessage id="insights.operations.header" />}
              backLink={indexLink}
              backLinkText={<FormattedMessage id="insights.title" />}
            >
              <InsightPageControls {...headerProps} />
            </PageHeader>
          ) : (
            <InsightPageHeader
              title={<FormattedMessage id="insights.operations.header" />}
              backLink={indexLink}
              backLinkText={<FormattedMessage id="insights.title" />}
              {...headerProps}
            />
          )}
          <OperationsPage {...pageProps} />
        </RouteWhenAuthenticated>
        <RouteWhenAuthenticated exact path={`${baseURI}/operations/rejected-purchases`}>
          {isExperienceFlow ? (
            <PageHeader
              title={<FormattedMessage id="insights.sales-by-venue.rejections" />}
              backLink={operationsLink}
              backLinkText={<FormattedMessage id="insights.operations.header" />}
            >
              <InsightPageControls {...headerProps} />
            </PageHeader>
          ) : (
            <InsightPageHeader
              title={<FormattedMessage id="insights.sales-by-venue.rejections" />}
              backLink={operationsLink}
              backLinkText={<FormattedMessage id="insights.operations.header" />}
              {...headerProps}
            />
          )}

          <RejectedPurchasesPage {...pageProps} />
        </RouteWhenAuthenticated>
        <RouteWhenAuthenticated exact path={`${baseURI}/menu`}>
          {isExperienceFlow ? (
            <PageHeader
              title={<FormattedMessage id="insights.menu.header" />}
              backLink={indexLink}
              backLinkText={<FormattedMessage id="insights.title" />}
            >
              <InsightPageControls {...headerProps} />
            </PageHeader>
          ) : (
            <InsightPageHeader
              title={<FormattedMessage id="insights.menu.header" />}
              backLink={indexLink}
              backLinkText={<FormattedMessage id="insights.title" />}
              {...headerProps}
            />
          )}
          <MenuPage {...pageProps} />
        </RouteWhenAuthenticated>
        <RouteWhenAuthenticated exact path={`${baseURI}/performance`}>
          {isExperienceFlow ? (
            <PageHeader
              title={<FormattedMessage id="insights.performance.header" />}
              backLink={indexLink}
              backLinkText={<FormattedMessage id="insights.title" />}
            >
              <InsightPageControls {...headerProps} />
            </PageHeader>
          ) : (
            <InsightPageHeader
              title={<FormattedMessage id="insights.performance.header" />}
              backLink={indexLink}
              backLinkText={<FormattedMessage id="insights.title" />}
              {...headerProps}
            />
          )}
          <PerformancePage {...pageProps} />
        </RouteWhenAuthenticated>
        <RouteWhenAuthenticated exact path={`${baseURI}/customer-base`}>
          {isExperienceFlow ? (
            <PageHeader
              title={<FormattedMessage id="insights.customer-base.header" />}
              backLink={indexLink}
              backLinkText={<FormattedMessage id="insights.title" />}
            >
              <InsightPageControls {...headerProps} />
            </PageHeader>
          ) : (
            <InsightPageHeader
              title={<FormattedMessage id="insights.customer-base.header" />}
              backLink={indexLink}
              backLinkText={<FormattedMessage id="insights.title" />}
              {...headerProps}
            />
          )}

          <CustomerBasePage {...pageProps} />
        </RouteWhenAuthenticated>
        <RouteWhenAuthenticated exact path={`${baseURI}/ratings`}>
          {isExperienceFlow ? (
            <PageHeader
              title={<FormattedMessage id="insights.ratings-and-reviews.header" />}
              backLink={indexLink}
              backLinkText={<FormattedMessage id="insights.title" />}
            >
              <InsightPageControls {...headerProps} />
            </PageHeader>
          ) : (
            <InsightPageHeader
              title={<FormattedMessage id="insights.ratings-and-reviews.header" />}
              backLink={indexLink}
              backLinkText={<FormattedMessage id="insights.title" />}
              {...headerProps}
            />
          )}

          <RatingsAndReviewsPage {...pageProps} />
        </RouteWhenAuthenticated>
        <RouteWhenAuthenticated path={`${baseURI}/reports`}>
          {isExperienceFlow ? (
            <PageHeader
              title={<FormattedMessage id="insights.reports.header" />}
              backLink={indexLink}
              backLinkText={<FormattedMessage id="insights.title" />}
            >
              <InsightPageControls {...headerProps} />
            </PageHeader>
          ) : (
            <InsightPageHeader
              title={<FormattedMessage id="insights.reports.header" />}
              backLink={indexLink}
              backLinkText={<FormattedMessage id="insights.title" />}
              {...headerProps}
            />
          )}

          <ReportsPage {...pageProps} />
        </RouteWhenAuthenticated>
      </Switch>
    </Root>
  );
};

export default InsightRoutes;
